import { useEffect, useMemo, useState, useCallback, useRef } from "react";
import { http, warnZeroStock, barcodeQty } from "@/lib/api";
import { useAuth } from "@/lib/AuthContext";
import { printOrder } from "@/lib/printOrder";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Card } from "@/components/ui/card";
import {
  X,
  Send,
  User as UserIcon,
  Search,
  MessageSquarePlus,
  Clock,
} from "lucide-react";
import { toast } from "sonner";
import ProductCatalogPanel from "@/components/ProductCatalogPanel";
import DeliveryCustomerDialog from "@/components/DeliveryCustomerDialog";
import Numpad from "@/components/Numpad";
import CommentInlinePanel from "@/components/CommentInlinePanel";
import SimpleCommentDialog from "@/components/SimpleCommentDialog";
import CustomerPendingOrderDialog from "@/components/CustomerPendingOrderDialog";
import { CartRow } from "@/pages/POSRetail";
import { parseScaleBarcode, findProductForScaleCode } from "@/lib/scaleBarcode";
import { useKeyboard } from "@/lib/KeyboardContext";
import { addHold } from "@/lib/deliveryHolds";

const COMMENT_PREFIXES = ["ΜΕ", "ΧΩΡΙΣ", "ΛΙΓΟ", "ΠΟΛΥ", "ΜΟΝΟ", "EXTRA"];

function _formatCommentLabel(c, qty, prefix) {
  if (qty === -1) return `ΧΩΡΙΣ ${c.name}`;
  if (qty > 1) return prefix ? `${qty}× ${prefix} ${c.name}` : `${qty}× ${c.name}`;
  return prefix ? `${prefix} ${c.name}` : c.name;
}

/**
 * Delivery — Νέα Παραγγελία editor.
 *
 * Full-screen, layout mirrors the Restaurant table-order editor so the
 * waiter's muscle memory stays intact. Visually identical; backend &
 * actions are delivery-specific (no link with restaurant tables).
 *
 *  Layout
 *  ───────────────────────────────────────────────────────────
 *  │ Top bar:  ← Επιστροφή   |   Νέα Παραγγελία (Delivery)    │
 *  │           |   👤 Πελάτης chip                            │
 *  ├──────────────────────────┬─────────────────────────────  │
 *  │ Cart items (left)        │  Product catalog (right)      │
 *  │                          │                                │
 *  ├──────────────────────────┴────────────────────────────  │
 *  │ Σύνολο · qty/+/- · «Σχόλιο» · «Διαγραφή» · «Πελάτης»     │
 *  │ · «Καταχώρηση»                                          │
 *  └────────────────────────────────────────────────────────
 *
 * Submission: when the operator taps «Καταχώρηση», POST /delivery/orders
 * with the cart items + chosen customer + selected address. The cashier
 * lands back on the delivery list. (Kitchen-print routing will plug in
 * later — see PRD.)
 */
export default function DeliveryNewOrder({ open, onClose, onSubmitted, drivers = [], order = null, template = null, activeOrders = [], onSwitchToExisting = null, onHeld = null }) {
  const { user } = useAuth();
  const { open: oskOpen, setOpen: setOskOpenCtx } = useKeyboard();
  // Duplicate mode = `template` passed but no `order`. Behaves like a
  // brand-new order with the cart + customer pre-populated from the
  // template; submit POSTs (does NOT PUT) so the original DELIVERED /
  // CANCELLED entry stays untouched.
  const isEdit = !!order;
  const sourceForPrefill = order || template;
  const [products, setProducts] = useState([]);
  const [categories, setCategories] = useState([]);
  const [printers, setPrinters] = useState([]);
  const [activeCat, setActiveCat] = useState("all");
  const [settings, setSettings] = useState(null);
  const [search, setSearch] = useState("");
  const [items, setItems] = useState([]);
  const [selectedIdx, setSelectedIdx] = useState(-1);
  const [customer, setCustomer] = useState(null);   // CRM customer doc
  const [address, setAddress] = useState(null);     // chosen address from customer
  const [pickerOpen, setPickerOpen] = useState(false);
  // «Εκκρεμή παραγγελία» dialog when the picked customer already has
  // an ACTIVE order (NEW / DISPATCHED). Surfaces immediately after
  // ``onSelect`` so the operator can choose between starting a fresh
  // entry or appending to the existing one.
  const [pendingCheck, setPendingCheck] = useState({ open: false, customer: null, orders: [] });
  // When the operator clicks the already-selected customer card we open
  // the picker pre-loaded on the customer's profile (step="confirm").
  // Stored as a string so the picker's useEffect re-runs each click.
  const [pickerInitialId, setPickerInitialId] = useState(null);
  const [busy, setBusy] = useState(false);
  const [notes, setNotes] = useState("");
  const [driverId, setDriverId] = useState("");
  const [commentEdit, setCommentEdit] = useState({ open: false, idx: null, value: "" });
  // Snapshot of the order items as they were when the editor opened.
  // Used to compute the «delta» kitchen-print on edit submit (only new
  // additions + cancellations / qty changes are printed — never the
  // unchanged base). Captured once at mount via the prefill effect.
  const originalItemsRef = useRef([]);
  // Restaurant-style comment dialog state.
  const [commentDialog, setCommentDialog] = useState({
    open: false,
    idx: null,
    submittedKey: null,   // unused on delivery (we have no submitted batches)
    selections: {},
    prefixes: {},
    activePrefix: null,
    freeText: "",
  });
  const [comments, setComments] = useState([]);
  const [commentGroups, setCommentGroups] = useState([]);
  // Floating numpad used by CartRow for qty/price/discount edits.
  const [numpad, setNumpad] = useState({ open: false, target: null, label: "", value: "", allowDot: true });
  const openNumpad = (target, label, initial = "", allowDot = true) =>
    setNumpad({ open: true, target, label, value: String(initial ?? ""), allowDot });
  const closeNumpad = () => setNumpad((n) => ({ ...n, open: false }));

  // -----------------------------------------------------------------
  // Initial data load — products & categories & settings.
  useEffect(() => {
    if (!open) return;
    (async () => {
      try {
        const [pRes, cRes, sRes, cmRes, cgRes, prRes] = await Promise.all([
          http.get("/products?channel=delivery"),
          http.get("/categories"),
          http.get("/settings"),
          http.get("/comments"),
          http.get("/comment-groups"),
          http.get("/printers"),
        ]);
        setProducts(pRes.data || []);
        setCategories(cRes.data || []);
        setSettings(sRes.data || null);
        setComments(cmRes.data || []);
        setCommentGroups(cgRes.data || []);
        setPrinters(prRes.data || []);
      } catch (e) {
        toast.error("Αποτυχία φόρτωσης καταλόγου");
      }
    })();
    // Reset state on each open. When editing OR duplicating, pre-fill
    // the cart + customer from the source order.
    if (sourceForPrefill) {
      const src = sourceForPrefill;
      const repItems = (src.items || []).map((it) => ({
        _pid: `edit-${Math.random().toString(16).slice(2, 8)}`,
        product_id: it.product_id || null,
        description: it.description,
        qty: Number(it.qty || 1),
        price: Number(it.price || 0),
        vat_rate: it.vat_rate,
        department: it.department,
        allow_comment: it.allow_comment !== false,
        comment_group_ids: it.comment_group_ids || [],
        free_text: it.free_text || null,
        comment: it.comment || "",
        comments: it.comments || [],
        modifier_total: Number(it.modifier_total || 0),
        discount_percent: it.discount_percent ?? null,
        no_discount: !!it.no_discount,
      }));
      setItems(repItems);
      // Capture immutable snapshot for the edit-submit diff. Cloned so
      // future mutations on `items` don't leak into the reference set.
      if (order) {
        originalItemsRef.current = (order.items || []).map((it) => ({
          product_id: it.product_id || null,
          description: it.description,
          qty: Number(it.qty || 1),
          price: Number(it.price || 0),
          comments: it.comments || [],
          free_text: it.free_text || null,
        }));
      } else {
        originalItemsRef.current = [];
      }
      setSelectedIdx(-1);
      // Synthesize the customer-pill from the order. We then refetch
      // the full customer card so the chip can be tapped to edit it.
      setCustomer(src.customer_id ? {
        id: src.customer_id,
        name: src.customer_name || "—",
        code: "",
        phones: src.phone ? [src.phone] : [],
        addresses: [],
      } : null);
      setAddress(src.address
        ? (typeof src.address === "string" ? { area: src.address } : src.address)
        : null);
      // Duplicates re-use the cart but drop the previous driver & notes
      // (the «Επανάληψη» is a fresh order — let the operator pick again).
      // Holds, however, restore the operator's prior in-progress state
      // verbatim. We distinguish via `_resumed_hold:true` on the template.
      const isResumedHold = !!template && !order && template._resumed_hold;
      const isDuplicate = !!template && !order && !isResumedHold;
      setNotes(isDuplicate ? "" : (src.notes || ""));
      setDriverId(isDuplicate ? "" : (src.driver_id || ""));
      if (src.customer_id) {
        http.get(`/customers/${src.customer_id}`).then((r) => setCustomer(r.data)).catch(() => {});
      }
    } else {
      setItems([]);
      originalItemsRef.current = [];
      setSelectedIdx(-1);
      setCustomer(null);
      setAddress(null);
      setNotes("");
      setDriverId("");
    }
    setSearch("");
    setActiveCat("all");
  }, [open, order, template]);

  // -----------------------------------------------------------------
  // Cart operations
  const addProduct = useCallback((p, addQty = 1) => {
    warnZeroStock(p);
    // Compute the affected index from the CURRENT items closure so we
    // don't rely on assignment timing within the setItems updater.
    const existingIdx = items.findIndex((x) => x._pid === p.id && !x.free_text && (!x.comments || x.comments.length === 0));
    const newIdx = existingIdx >= 0 ? existingIdx : items.length;

    setItems((prev) => {
      const idx = prev.findIndex((x) => x._pid === p.id && !x.free_text && (!x.comments || x.comments.length === 0));
      if (idx >= 0) {
        const cp = [...prev];
        cp[idx] = { ...cp[idx], qty: Number(cp[idx].qty || 1) + addQty };
        return cp;
      }
      return [
        ...prev,
        {
          _pid: p.id,
          product_id: p.id,
          description: p.description,
          qty: addQty,
          price: Number(p.price || 0),
          vat_rate: p.vat_rate,
          department: p.department,
          allow_comment: !!p.allow_comment,
          comment_group_ids: p.comment_group_ids || [],
          no_discount: !!p.no_discount,
          free_text: null,
          comment: "",
          comments: [],
          modifier_total: 0,
          discount_percent: null,
        },
      ];
    });
    // Auto-select the affected row so its action bar (qty/price/disc/
    // remove/comment) is the ONLY one open — mirrors Retail/Restaurant.
    setSelectedIdx(newIdx);
    // Auto-open the appropriate comment editor when flagged. Use the
    // simple free-text dialog when no groups are linked (otherwise the
    // rich panel would surface unrelated categories — user-reported bug).
    if (p.allow_comment && p.auto_open_comment) {
      const groupIds = p.comment_group_ids || [];
      setTimeout(() => {
        if (groupIds.length === 0) {
          setSimpleComment({ open: true, idx: newIdx });
        } else {
          setCommentDialog({
            open: true,
            idx: newIdx,
            submittedKey: null,
            selections: {},
            prefixes: {},
            activePrefix: null,
            freeText: "",
          });
        }
      }, 0);
    }
  }, [items]);

  const incQty = (idx) => setItems((arr) => arr.map((it, i) => (i === idx ? { ...it, qty: Number(it.qty || 1) + 1 } : it)));
  const decQty = (idx) => setItems((arr) => arr.map((it, i) => (i === idx ? { ...it, qty: Math.max(1, Number(it.qty || 1) - 1) } : it)));
  const removeItem = (idx) => setItems((arr) => arr.filter((_, i) => i !== idx));
  const updateComment = (idx, text) => setItems((arr) => arr.map((it, i) => (i === idx ? { ...it, free_text: text || null, comment: text || null } : it)));
  const updateLine = useCallback((idx, patch) => {
    setItems((arr) => arr.map((it, i) => (i === idx ? { ...it, ...patch } : it)));
  }, []);

  // ---- Comment-panel handlers (Restaurant parity) ----
  const togglePrefix = (p) => {
    setCommentDialog((d) => ({ ...d, activePrefix: d.activePrefix === p ? null : p }));
  };
  const toggleCommentTile = (c) => {
    setCommentDialog((d) => {
      const sel = { ...d.selections };
      const pref = { ...(d.prefixes || {}) };
      if (sel[c.id]) {
        delete sel[c.id];
        delete pref[c.id];
        return { ...d, selections: sel, prefixes: pref };
      }
      if (c.radio_group && !c.radio_multiple) {
        comments
          .filter((x) => x.group_id === c.group_id && x.radio_group === c.radio_group && !x.splittable)
          .forEach((x) => { delete sel[x.id]; delete pref[x.id]; });
      }
      sel[c.id] = 1;
      if (d.activePrefix) pref[c.id] = d.activePrefix;
      return { ...d, selections: sel, prefixes: pref, activePrefix: null };
    });
  };
  const incrementSplit = (c) => {
    setCommentDialog((d) => {
      const sel = { ...d.selections };
      const cur = sel[c.id] || 0;
      if (cur === -1) delete sel[c.id]; // από «ΧΩΡΙΣ» το + πάει στο 0 (χωρίς χρέωση)
      else sel[c.id] = cur + 1;
      return { ...d, selections: sel };
    });
  };
  const decrementSplit = (c) => {
    setCommentDialog((d) => {
      const sel = { ...d.selections };
      const cur = sel[c.id] || 0;
      if (cur === 0) sel[c.id] = -1;
      else if (cur === 1) delete sel[c.id];
      else if (cur === -1) { /* stop */ }
      else sel[c.id] = cur - 1;
      return { ...d, selections: sel };
    });
  };
  const saveComment = () => {
    if (commentDialog.idx == null) return;
    const out = [];
    let modTotal = 0;
    Object.entries(commentDialog.selections).forEach(([cid, qty]) => {
      const c = comments.find((x) => x.id === cid);
      if (!c || qty === 0) return;
      const effectiveQty = Number(qty) || 0;
      const prefix = commentDialog.prefixes?.[cid] || null;
      out.push({
        id: c.id, name: c.name,
        label: _formatCommentLabel(c, effectiveQty, prefix),
        prefix, price_delta: c.price_delta, is_deduction: !!c.is_deduction,
        color: c.color, radio_group: c.radio_group, splittable: c.splittable,
        quantity: effectiveQty,
      });
      const sign = c.is_deduction ? -1 : 1;
      if (effectiveQty > 0) modTotal += sign * Number(c.price_delta || 0) * effectiveQty;
      else if (effectiveQty === -1 && c.is_deduction) modTotal -= Number(c.price_delta || 0);
    });
    const freeText = (commentDialog.freeText || "").trim();
    const labels = out.map((c) => c.label);
    if (freeText) labels.push(freeText);
    updateLine(commentDialog.idx, {
      comments: out,
      comment: labels.join(", "),
      free_text: freeText || null,
      modifier_total: modTotal,
    });
    setCommentDialog({ open: false, idx: null, submittedKey: null, selections: {}, prefixes: {}, activePrefix: null, freeText: "" });
  };
  // Free-text-only comment dialog (large pop-up) — used when the item has
  // ``allow_comment=true`` but NO ``comment_group_ids``. Showing the rich
  // tile panel for these items would surface unrelated categories.
  const [simpleComment, setSimpleComment] = useState({ open: false, idx: null });

  // Opens the comment editor for an existing cart line. Picks the simple
  // free-text dialog when no comment groups are linked, else the rich
  // CommentInlinePanel. Re-hydrates the previous selections / free text.
  const openCommentDialogForIdx = (idx) => {
    const it = items[idx];
    if (!it) return;
    const groupIds = it.comment_group_ids || [];
    if (groupIds.length === 0) {
      setSimpleComment({ open: true, idx });
      return;
    }
    const sel = {};
    const pref = {};
    (it.comments || []).forEach((c) => {
      sel[c.id] = c.quantity || 1;
      if (c.prefix) pref[c.id] = c.prefix;
    });
    setCommentDialog({ open: true, idx, submittedKey: null, selections: sel, prefixes: pref, activePrefix: null, freeText: it.free_text || "" });
  };

  // Virtual `tbl` shape — CommentInlinePanel reads `open_items[idx]` to
  // display the item's description and resolve its allowed groups.
  const virtualTbl = useMemo(() => ({ open_items: items, submitted_batches: [] }), [items]);

  // Numpad accept — mirrors POSRetail.onNumpadAccept for qty/price/disc.
  const onNumpadAccept = (parsed) => {
    const t = numpad.target;
    if (t && typeof t === "object" && t.kind && t.idx != null) {
      const { kind, idx } = t;
      if (parsed == null) { closeNumpad(); return; }
      if (kind === "qty") updateLine(idx, { qty: Math.max(0.01, parsed) });
      if (kind === "price") {
        const prodP = products.find((p) => p.id === items[idx]?._pid);
        if (prodP && prodP.allow_price_change === false) {
          toast.error("Δεν επιτρέπεται αλλαγή τιμής σε αυτό το είδος");
          closeNumpad();
          return;
        }
        updateLine(idx, { price: Math.max(0, parsed) });
      }
      if (kind === "disc") {
        const it = items[idx];
        let pct = Math.max(0, parsed);
        if (pct === 0) {
          updateLine(idx, { discount_percent: null });
        } else {
          let capped = Math.min(99, pct);
          const gross = (it.qty || 0) * (it.price || 0);
          if (gross > 0) {
            const maxByNet = ((gross - 0.01) / gross) * 100;
            if (capped > maxByNet) capped = Math.max(0, maxByNet);
          }
          capped = Math.round(capped * 100) / 100;
          if (capped < pct) toast.info(`Έκπτωση προσαρμόστηκε σε ${capped}%`);
          updateLine(idx, { discount_percent: capped > 0 ? capped : null });
        }
      }
    }
    closeNumpad();
  };

  const total = useMemo(() => items.reduce((s, it) => s + Number(it.qty || 1) * (Number(it.price || 0) + Number(it.modifier_total || 0)), 0), [items]);
  const itemsCount = useMemo(() => items.reduce((s, it) => s + Number(it.qty || 1), 0), [items]);

  // -----------------------------------------------------------------
  // Submit ----------------------------------------------------------
  // Strict category-routing for Delivery — mirrors POSRetail's helper.
  // Each item routes to its category's `printer_id` / `printer_ids[0]`
  // ONLY when `category.print_enabled !== false`. Items without an
  // assigned printer are SKIPPED per Feb 2026 spec («ΑΝ ΕΝΑ ΕΙΔΟΣ
  // ΔΕΝ ΕΧΕΙ ΕΚΤΥΠΩΤΗ ΔΕΝ ΘΑ ΤΥΠΩΘΕΙ»).
  const sendItemsToPrepPrinters = async (createdOrder, listOverride = null, opts = {}) => {
    const list = listOverride !== null ? listOverride : items;
    if (!list || list.length === 0) return;
    const activePrinters = (printers || []).filter((p) => p.active);
    if (activePrinters.length === 0) return;
    const catPrinter = {};
    categories.forEach((c) => {
      if (c.print_enabled === false) return;
      const pid = c.printer_id || (Array.isArray(c.printer_ids) && c.printer_ids[0]) || null;
      if (pid) catPrinter[c.id] = pid;
    });
    const groups = new Map();
    for (const it of list) {
      const prod = (it.product_id && products.find((p) => p.id === it.product_id))
        || products.find((p) => p.description === it.description)
        || {};
      const targetId = catPrinter[prod.category_id];
      if (!targetId) continue;
      const target = activePrinters.find((p) => p.id === targetId);
      if (!target) continue;
      if (!groups.has(target.id)) groups.set(target.id, { profile: target, items: [] });
      groups.get(target.id).items.push(it);
    }
    if (groups.size === 0) return;
    const orderNo = createdOrder?.order_no || createdOrder?.global_order_no || null;
    const names = [];
    for (const { profile, items: gItems } of groups.values()) {
      const total = gItems.reduce(
        (s, it) => s + (Number(it.qty) || 1) * ((Number(it.price) || 0) + (Number(it.modifier_total) || 0)),
        0,
      );
      try {
        await printOrder({
          businessLines: settings?.business_lines || [],
          businessLinesBold: settings?.business_lines_bold || [],
          items: gItems,
          total,
          printerName: profile.windows_printer_name || profile.name || "",
          printerType: profile.printer_type || "kitchen",
          globalOrderNo: orderNo,
          module: "DELIVERY",
          orderNo,
          operatorName: user?.name || user?.username || null,
          correction: !!opts.correction,
        });
        names.push(profile.name);
      } catch { /* per-printer failure silent */ }
    }
    if (names.length) {
      const label = opts.correction ? "Διόρθωση" : "🖨️ Στάλθηκαν στα παρασκευαστήρια";
      toast.success(`${label}: ${names.join(", ")}`);
    }
  };

  // ---------------------------------------------------------------
  // Edit-mode diff: compute the items that should print on the
  // kitchen printers when the operator saves a correction. Returns
  // `{ added, voided }` arrays where `qty` is always the delta (>=1).
  // Identity is the «description + comments fingerprint» triple so
  // an item with the same description but different comments counts
  // as a distinct line (mirrors what the kitchen actually plates).
  // ---------------------------------------------------------------
  const computeEditDelta = (originalList, newList) => {
    const fp = (it) => {
      const desc = (it.description || "").trim();
      const cmt = Array.isArray(it.comments)
        ? it.comments.map((c) => (c?.label || c?.name || "")).join("|")
        : "";
      const ft = (it.free_text || "").trim();
      return `${desc}@@${cmt}@@${ft}`;
    };
    const agg = (arr) => {
      const m = new Map();
      for (const it of arr || []) {
        const k = fp(it);
        const q = Number(it.qty || 1);
        if (!m.has(k)) m.set(k, { ...it, qty: 0 });
        m.get(k).qty += q;
      }
      return m;
    };
    const orig = agg(originalList);
    const next = agg(newList);
    const added = [];
    const voided = [];
    const seen = new Set();
    for (const [k, it] of next) {
      seen.add(k);
      const prev = orig.get(k);
      const delta = it.qty - (prev ? prev.qty : 0);
      if (delta > 0) added.push({ ...it, qty: delta });
      else if (delta < 0) voided.push({ ...it, qty: -delta, is_void: true });
    }
    for (const [k, it] of orig) {
      if (seen.has(k)) continue;
      voided.push({ ...it, qty: it.qty, is_void: true });
    }
    return { added, voided };
  };

  // ---------------------------------------------------------------
  // Hold («Αναμονή») — snapshot the current draft to localStorage and
  // close the editor so the operator can take a fresh order. Only
  // available in NEW-order mode (isEdit=false); edits stay locked to
  // their MongoDB row so we don't risk losing the prior submission.
  // ---------------------------------------------------------------
  const holdCurrent = () => {
    if (isEdit) {
      toast.error("Δεν υποστηρίζεται σε λειτουργία διόρθωσης");
      return;
    }
    if (items.length === 0) {
      toast.error("Καλάθι κενό");
      return;
    }
    try {
      addHold({ items, customer, address, notes, driverId });
      toast.success("Σε αναμονή");
      try { onHeld?.(); } catch { /* noop */ }
      onClose?.();
    } catch (e) {
      toast.error("Αποτυχία αποθήκευσης σε αναμονή");
    }
  };

  const submit = async () => {
    if (!customer) {
      toast.error("Επιλέξτε πρώτα πελάτη");
      setPickerOpen(true);
      return;
    }
    if (items.length === 0) {
      toast.error("Δεν υπάρχουν είδη στην παραγγελία");
      return;
    }
    setBusy(true);
    try {
      const adr = address;
      const streetParts = adr ? [adr.street, adr.street_no].filter(Boolean).join(" ") : [customer.street, customer.street_no].filter(Boolean).join(" ");
      const areaParts = adr ? [adr.area, adr.floor && `όρ. ${adr.floor}`, adr.doorbell && `κορδ. ${adr.doorbell}`].filter(Boolean).join(", ") : "";
      const composed = [streetParts, areaParts].filter(Boolean).join(" — ") || (isEdit ? order.address : "");
      const payload = {
        customer_id: customer.id,
        customer_name: customer.name,
        phone: (customer.phones || [])[0] || (isEdit ? order.phone : ""),
        address: composed,
        notes: notes || adr?.instructions || customer?.note || null,
        items: items.map(({ _pid, allow_comment, ...rest }) => rest),
        driver_id: driverId || null,
      };
      if (isEdit) {
        await http.put(`/delivery/orders/${order.id}`, payload);
        toast.success("Ενημερώθηκε η παραγγελία");
        // Delta print on edit — only added items + cancellations.
        // 1) Print new items (or quantity increases) as a normal
        //    kitchen ticket so the cook gets a fresh slip.
        // 2) Print voided items (or quantity decreases) as a
        //    «ΔΙΟΡΘΩΣΗ ΠΑΡΑΓΓΕΛΙΑΣ» ticket with ΑΚΥΡΩΘΗΚΕ rows.
        try {
          const { added, voided } = computeEditDelta(originalItemsRef.current, items);
          if (added.length) {
            await sendItemsToPrepPrinters(order, added, { correction: false });
          }
          if (voided.length) {
            await sendItemsToPrepPrinters(order, voided, { correction: true });
          }
        } catch { /* noop */ }
      } else {
        const r = await http.post("/delivery/orders", { ...payload, dispatch: false });
        toast.success("Καταχωρήθηκε η παραγγελία");
        // Fire-and-forget kitchen routing — strict category filter,
        // items without a printer assignment are skipped (per spec).
        try { await sendItemsToPrepPrinters(r.data); } catch { /* noop */ }
      }
      onSubmitted?.();
      onClose?.();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Αποτυχία καταχώρησης");
    } finally {
      setBusy(false);
    }
  };

  if (!open) return null;

  return (
    <div className="fixed inset-0 z-50 bg-slate-100 flex flex-col" data-testid="delivery-new-order">
      {/* Main grid: cart left (narrower), catalog right - */}
      <div className="flex-1 grid grid-cols-[560px_1fr] min-h-0">
        {/* LEFT PANEL --------------------------------------------- */}
        <div className="bg-white border-r-2 border-slate-300 flex flex-col min-h-0">
          {/* Customer info card — clickable. If a customer is already
              selected, opens the picker pre-loaded on their profile (so
              the operator can review/edit addresses and tap «Αλλαγή
              Πελάτη» to switch). Otherwise opens the picker normally. */}
          <button
            type="button"
            onClick={() => {
              setPickerInitialId(customer?.id || null);
              setPickerOpen(true);
            }}
            data-testid="delivery-new-customer-card"
            className={`w-full text-left px-5 py-4 border-b-2 transition-colors ${
              customer
                ? "bg-blue-50 border-blue-200 hover:bg-blue-100"
                : "bg-amber-50 border-amber-300 hover:bg-amber-100 animate-pulse"
            }`}
          >
            {customer ? (
              <div className="flex items-start gap-3">
                <div className="w-12 h-12 rounded-full bg-blue-600 text-white grid place-items-center shrink-0">
                  <UserIcon size={24} />
                </div>
                <div className="flex-1 min-w-0">
                  <div className="flex items-baseline gap-3 flex-wrap">
                    <span className="text-2xl font-bold text-slate-900 truncate" data-testid="delivery-new-customer-pill">{customer.name}</span>
                    {customer.code && <span className="text-sm font-mono text-blue-700 bg-white px-2 py-0.5 rounded border border-blue-200">#{customer.code}</span>}
                    {customer.vat_no && <span className="text-xs font-mono text-slate-600">ΑΦΜ {customer.vat_no}</span>}
                  </div>
                  {(customer.phones || []).length > 0 && (
                    <div className="text-base text-slate-700 font-mono mt-1 flex items-center gap-3 flex-wrap">
                      <span>📞 {(customer.phones || []).join(" · ")}</span>
                    </div>
                  )}
                  {(address || isEdit) && (
                    <div className="text-base text-slate-700 mt-0.5 truncate">
                      📍 {address
                        ? [
                            [address.street, address.street_no].filter(Boolean).join(" "),
                            address.area,
                            address.floor && `όρ. ${address.floor}`,
                            address.doorbell && `κορδ. ${address.doorbell}`,
                          ].filter(Boolean).join(", ")
                        : (isEdit && order?.address) || "—"}
                    </div>
                  )}
                  {(address?.instructions || customer.note) && (
                    <div className="text-sm italic text-slate-600 mt-1 truncate">› {address?.instructions || customer.note}</div>
                  )}
                </div>
              </div>
            ) : (
              <div className="flex items-center gap-3">
                <div className="w-12 h-12 rounded-full bg-amber-500 text-white grid place-items-center shrink-0">
                  <UserIcon size={24} />
                </div>
                <div>
                  <div className="text-2xl font-bold text-amber-900">ΕΠΙΛΟΓΗ ΠΕΛΑΤΗ</div>
                  <div className="text-sm text-amber-800">υποχρεωτικό — πάτα εδώ για αναζήτηση</div>
                </div>
              </div>
            )}
          </button>

          {/* «ΝΕΑ ΕΙΔΗ» strip */}
          <div className="px-4 py-2 text-xs font-bold uppercase tracking-wider text-slate-700 bg-amber-50 border-b border-amber-200 shrink-0">
            Νέα Είδη {items.length > 0 ? `(${items.length})` : "— πρόσθεσε από τα δεξιά"}
          </div>

          {/* Items list */}
          <div className="flex-1 overflow-y-auto" data-testid="delivery-new-cart">
            {items.length === 0 && (
              <div className="h-32 grid place-items-center text-slate-400 text-sm italic">
                Πρόσθεσε είδη από τη δεξιά λίστα
              </div>
            )}
            {items.map((it, i) => (
              <CartRow
                key={i}
                item={{ ...it, comment: it.comment || it.free_text, allow_comment: it.allow_comment !== false }}
                idx={i}
                selected={selectedIdx === i}
                onSelect={() => setSelectedIdx(selectedIdx === i ? -1 : i)}
                onChange={(patch) => updateLine(i, patch)}
                onRemove={() => removeItem(i)}
                openNumpad={openNumpad}
                onCommentClick={(idx) => openCommentDialogForIdx(idx)}
                testid={`delivery-new-row-${i}`}
              />
            ))}
          </div>

          {/* Search bar above total — matches Restaurant position */}
          <div className="px-5 pt-2 pb-1 border-t-2 border-slate-300 bg-white">
            <div className="relative">
              <Search size={18} className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400 pointer-events-none" />
              <input
                type="text"
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                onKeyDown={(e) => {
                  if (e.key !== "Enter") return;
                  e.preventDefault();
                  const v = (search || "").trim();
                  if (!v) return;
                  // Scale EAN-13 — checked first so the prefix wins
                  // over the regular barcode-matching fallback.
                  const scaled = parseScaleBarcode(v, settings);
                  if (scaled) {
                    const prod = findProductForScaleCode(products, scaled.product_code);
                    if (!prod) {
                      toast.error(`Ζυγός: δεν βρέθηκε προϊόν με κωδικό ${scaled.product_code}`);
                      return;
                    }
                    if (prod.is_weighable) {
                      let qty = 1, unit = Number(prod.price) || 0;
                      if (scaled.mode === "weight") {
                        qty = Number(scaled.value.toFixed(3));
                      } else if (unit > 0) {
                        qty = Number((scaled.value / unit).toFixed(3));
                      }
                      setItems((prev) => [
                        ...prev,
                        {
                          _pid: prod.id, product_id: prod.id, description: prod.description,
                          qty, price: unit, vat_rate: prod.vat_rate, department: prod.department,
                          allow_comment: !!prod.allow_comment, comment_group_ids: prod.comment_group_ids || [],
                          no_discount: !!prod.no_discount,
                          free_text: null, comment: "", comments: [], modifier_total: 0,
                          discount_percent: null, is_weighable: true,
                        },
                      ]);
                      setSelectedIdx(items.length);
                      setSearch("");
                      return;
                    }
                    addProduct(prod);
                    setSearch("");
                    return;
                  }
                  // Fallback — exact barcode / 5-digit code match.
                  const padded = v.padStart(5, "0");
                  const found = products.find(
                    (p) =>
                      (p.barcode1 || "").trim() === v ||
                      (p.barcode2 || "").trim() === v ||
                      (p.barcode3 || "").trim() === v ||
                      (p.code || "") === v ||
                      (p.code || "") === padded,
                  );
                  if (found) {
                    addProduct(found, barcodeQty(found, v));
                    setSearch("");
                  }
                }}
                placeholder="Αναζήτηση: περιγραφή, κωδικός ή barcode…"
                data-testid="delivery-new-search"
                className="w-full h-11 pl-10 pr-10 rounded-md border-2 border-slate-200 bg-white text-base focus:outline-none focus:border-blue-500 focus:ring-2 focus:ring-blue-200"
              />
              {search && (
                <button type="button" onClick={() => setSearch("")} className="absolute right-2 top-1/2 -translate-y-1/2 w-7 h-7 rounded-md grid place-items-center text-slate-500 hover:bg-slate-100">
                  <X size={16} />
                </button>
              )}
            </div>
          </div>

          {/* Total line + 2 action buttons (Cancel | Submit) */}
          <div className="bg-slate-50 px-5 pt-2 pb-3 space-y-2 border-t border-slate-200 shrink-0">
            <div className="flex justify-between items-baseline">
              <span className="text-2xl font-heading font-bold">Σύνολο παραγγελίας</span>
              <span className="text-3xl font-mono font-bold" data-testid="delivery-new-total">{total.toFixed(2)}€</span>
            </div>
            <div className="grid grid-cols-3 gap-2">
              <Button
                onClick={onClose}
                className="h-14 bg-red-600 hover:bg-red-700 text-white font-bold leading-tight whitespace-normal text-sm"
                data-testid="delivery-new-cancel"
              >
                <X size={16} className="mr-1 shrink-0" />
                <span className="flex flex-col items-start text-left">
                  <span>Ακύρωση</span>
                </span>
              </Button>
              <Button
                onClick={holdCurrent}
                disabled={isEdit || items.length === 0}
                title={isEdit ? "Δεν υποστηρίζεται σε λειτουργία διόρθωσης" : "Σε αναμονή"}
                className="h-14 bg-amber-500 hover:bg-amber-600 text-white font-bold leading-tight whitespace-normal text-sm disabled:opacity-40"
                data-testid="delivery-new-hold"
              >
                <Clock size={16} className="mr-1 shrink-0" />
                <span className="flex flex-col items-start text-left">
                  <span>Αναμονή</span>
                </span>
              </Button>
              <Button
                onClick={submit}
                disabled={busy || items.length === 0 || !customer}
                className="h-14 bg-emerald-600 hover:bg-emerald-700 text-white font-bold leading-tight whitespace-normal text-sm disabled:opacity-40"
                data-testid="delivery-new-submit"
              >
                <Send size={16} className="mr-1 shrink-0" />
                <span className="flex flex-col items-start text-left">
                  <span>{isEdit ? "Αποθήκευση" : "Καταχώρηση"}</span>
                  <span className="text-[10px] opacity-90 font-normal">{items.length} είδ.</span>
                </span>
              </Button>
            </div>
          </div>
        </div>

        {/* CATALOG (right) — switches to CommentInlinePanel when open --- */}
        <div className="bg-white overflow-hidden flex flex-col">
          {commentDialog.open ? (
            <CommentInlinePanel
              tbl={virtualTbl}
              commentDialog={commentDialog}
              setCommentDialog={setCommentDialog}
              comments={comments}
              commentGroups={commentGroups}
              settings={settings}
              COMMENT_PREFIXES={COMMENT_PREFIXES}
              togglePrefix={togglePrefix}
              toggleCommentTile={toggleCommentTile}
              incrementSplit={incrementSplit}
              decrementSplit={decrementSplit}
              saveComment={saveComment}
              oskOpen={oskOpen}
              setOskOpen={setOskOpenCtx}
            />
          ) : (
            <ProductCatalogPanel
              products={products}
              categories={categories}
              activeCat={activeCat}
              setActiveCat={setActiveCat}
              settings={settings}
              onProductClick={addProduct}
              testidPrefix="delivery-new"
              searchValue={search}
            />
          )}
        </div>
      </div>

      {/* Customer picker (rich, full-screen) ------------------------- */}
      <DeliveryCustomerDialog
        open={pickerOpen}
        onOpenChange={(o) => { setPickerOpen(o); if (!o) setPickerInitialId(null); }}
        initialCustomerId={pickerInitialId}
        onAddItem={(it) => {
          // Quick-add from history-preview or top-items in the picker.
          // Items come in receipt/top-item shape — same fields as CartItem.
          setItems((prev) => [
            ...prev,
            {
              _pid: `pick-${Math.random().toString(16).slice(2, 8)}`,
              description: it.description,
              qty: Number(it.qty || 1),
              price: Number(it.price || 0),
              vat_rate: it.vat_rate,
              department: it.department,
              allow_comment: true,
              no_discount: false,
              free_text: it.free_text || null,
              comment: it.free_text || null,
              modifier_total: Number(it.modifier_total || 0),
              discount_percent: it.discount_percent ?? null,
            },
          ]);
        }}
        onSelect={({ customer: c, address: a, lastOrder }) => {
          setCustomer(c);
          setAddress(a);
          // Quick Repeat — when the picker hands back a lastOrder, seed
          // the cart from it (replaces any in-progress lines).
          if (lastOrder?.found && Array.isArray(lastOrder.items)) {
            const repItems = lastOrder.items.map((it) => ({
              _pid: `repeat-${Math.random().toString(16).slice(2, 8)}`,
              description: it.description,
              qty: Number(it.qty || 1),
              price: Number(it.price || 0),
              vat_rate: it.vat_rate,
              department: it.department,
              allow_comment: true,
              free_text: it.free_text || null,
              modifier_total: Number(it.modifier_total || 0),
            }));
            setItems(repItems);
            toast.success(`Επανάληψη: ${c.name} · ${repItems.length} είδ. · ${Number(lastOrder.total_amount || 0).toFixed(2)}€`);
          } else {
            toast.success(`Πελάτης: ${c.name}`);
          }
          // Check for ACTIVE pending orders only on brand-new orders
          // (skip when editing or when ``onSwitchToExisting`` is not
          // wired — the prompt would have no follow-up action).
          if (!isEdit && onSwitchToExisting) {
            const pendings = (activeOrders || []).filter(
              (o) =>
                o.customer_id === c.id &&
                (o.status === "NEW" || o.status === "DISPATCHED")
            );
            if (pendings.length > 0) {
              setPendingCheck({ open: true, customer: c, orders: pendings });
            }
          }
        }}
      />

      {/* Comment edit dialog ---------------------------------------- */}
      {commentEdit.open && (
        <CommentEditDialog
          value={commentEdit.value}
          onSave={(v) => { updateComment(commentEdit.idx, v); setCommentEdit({ open: false, idx: null, value: "" }); }}
          onCancel={() => setCommentEdit({ open: false, idx: null, value: "" })}
        />
      )}

      {/* Floating numpad ---------------------------------------------- */}
      <Numpad
        open={numpad.open}
        label={numpad.label}
        value={numpad.value}
        allowDot={numpad.allowDot}
        onChange={(v) => setNumpad((n) => ({ ...n, value: v }))}
        onAccept={onNumpadAccept}
        onClose={closeNumpad}
      />

      {/* Free-text comment editor (no comment groups linked) */}
      <SimpleCommentDialog
        open={simpleComment.open}
        onOpenChange={(o) => setSimpleComment({ open: o, idx: o ? simpleComment.idx : null })}
        item={simpleComment.idx != null ? items[simpleComment.idx] : null}
        initialText={simpleComment.idx != null ? (items[simpleComment.idx]?.free_text || items[simpleComment.idx]?.comment || "") : ""}
        onSave={(text) => {
          if (simpleComment.idx == null) return;
          const trimmed = (text || "").trim();
          setItems((prev) =>
            prev.map((p, i) =>
              i === simpleComment.idx
                ? { ...p, free_text: trimmed || null, comment: trimmed, comments: [] }
                : p,
            ),
          );
        }}
      />

      {/* Customer pending-order check — when the operator picks a
          customer that already has an active order, this dialog
          lets them choose between starting fresh or appending. */}
      <CustomerPendingOrderDialog
        open={pendingCheck.open}
        onOpenChange={(o) => setPendingCheck((s) => ({ ...s, open: o }))}
        customerName={pendingCheck.customer?.name || ""}
        pendingOrders={pendingCheck.orders}
        onNew={() => setPendingCheck({ open: false, customer: null, orders: [] })}
        onAppend={(po) => {
          setPendingCheck({ open: false, customer: null, orders: [] });
          // Hand off control to the parent: close this editor and
          // reopen in edit-mode against the picked pending order.
          if (onSwitchToExisting) {
            onSwitchToExisting(po);
          }
        }}
      />
    </div>
  );
}

// -----------------------------------------------------------------
function CommentEditDialog({ value, onSave, onCancel }) {
  const [v, setV] = useState(value || "");
  return (
    <div className="fixed inset-0 z-[55] bg-black/40 grid place-items-center p-4">
      <Card className="w-full max-w-lg p-5 space-y-4">
        <h3 className="text-xl font-bold flex items-center gap-2">
          <MessageSquarePlus size={20} /> Σχόλιο είδους
        </h3>
        <Input
          autoFocus
          value={v}
          onChange={(e) => setV(e.target.value)}
          placeholder="π.χ. χωρίς κρεμμύδι, καλά ψημένο"
          maxLength={120}
          className="h-12 text-base"
          data-testid="delivery-new-comment-input"
        />
        <div className="flex justify-end gap-2">
          <Button variant="outline" size="lg" onClick={onCancel} data-testid="delivery-new-comment-cancel">
            <X size={16} className="mr-1" /> Άκυρο
          </Button>
          <Button size="lg" onClick={() => onSave(v)} className="bg-emerald-600 hover:bg-emerald-700" data-testid="delivery-new-comment-save">
            Αποθήκευση
          </Button>
        </div>
      </Card>
    </div>
  );
}
